/**
 * usePanelCounts — single source of truth for ALL sidebar/nav badge counts.
 *
 * Why this exists:
 *   Before this hook, the sidebar polled /api/approvals/count separately
 *   (every 60s) and nothing else. Tasks, Meetings, Inspections, Chat,
 *   Notifications, Stock Transfers had no badges at all. Each new badge
 *   would otherwise need its own polling effect — a recipe for race
 *   conditions and mismatched counts vs the OS app icon badge.
 *
 *   This hook calls ONE endpoint — `/api/notifications/panel-counts` —
 *   which returns every panel count derived from the same `notifications`
 *   collection that powers `useAppBadge`. So the red dot in the sidebar,
 *   the bell badge in the header, and the OS app icon badge can never
 *   drift out of sync.
 *
 * Triggers a refresh on:
 *   • mount
 *   • window focus
 *   • document visibility change (tab returns to foreground)
 *   • 60s poll while tab is visible
 *   • the global `pie:badge-invalidate` event (fired by lib/utils
 *     `apiRequest` after every mark-as-read, mark-all-read, chat read,
 *     approvals action)
 *
 * Returns: { notifications, tasks, meetings, inspections, chat,
 *            approvals, stock_transfers }
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/utils';

const EMPTY = {
  notifications: 0,
  tasks: 0,
  meetings: 0,
  inspections: 0,
  chat: 0,
  approvals: 0,
  stock_transfers: 0,
};

export default function usePanelCounts() {
  const { user } = useAuth();
  const [counts, setCounts] = useState(EMPTY);
  const inFlightRef = useRef(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const data = await apiRequest('/notifications/panel-counts');
      setCounts((prev) => ({ ...EMPTY, ...prev, ...data }));
    } catch (_) {
      /* swallow — sidebar badges are best effort */
    } finally {
      inFlightRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setCounts(EMPTY);
      return;
    }
    refresh();

    pollRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 60_000);

    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const onInvalidate = () => refresh();
    // Backwards compat for the legacy `approvals-updated` event still
    // dispatched by some action handlers — we want those to refresh
    // ALL panel counts now, not just approvals.
    const onApprovalsUpdated = () => refresh();

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pie:badge-invalidate', onInvalidate);
    window.addEventListener('approvals-updated', onApprovalsUpdated);
    // Refresh badges the moment a notification is read (bell close, nav click
    // marking a whole category, etc.)
    window.addEventListener('notifications:read', onInvalidate);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pie:badge-invalidate', onInvalidate);
      window.removeEventListener('approvals-updated', onApprovalsUpdated);
      window.removeEventListener('notifications:read', onInvalidate);
    };
  }, [user, refresh]);

  return counts;
}
